/**
 * Guest Sign-Up Modal — SeeTex Web
 *
 * Allows a guest (anonymous) user to link their account
 * to an email/password credential, preserving their work.
 */

import { useState, useCallback, type FormEvent } from "react";
import { useAuth } from "../contexts/AuthContext";
import { isValidEmail, validatePassword } from "../config/firebase";

interface GuestSignUpModalProps {
    onClose: () => void;
}

export function GuestSignUpModal({ onClose }: GuestSignUpModalProps) {
    const { linkGuest, signInWithGoogle, loading, error, clearError } = useAuth();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [displayName, setDisplayName] = useState("");
    const [localError, setLocalError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const displayError = localError || error;

    const handleSubmit = useCallback(
        async (e: FormEvent) => {
            e.preventDefault();
            setLocalError(null);

            const trimmedEmail = email.trim().toLowerCase();
            if (!isValidEmail(trimmedEmail)) {
                setLocalError("Please enter a valid email address");
                return;
            }

            const validation = validatePassword(password);
            if (!validation.valid) {
                setLocalError(validation.errors[0]);
                return;
            }

            await linkGuest(trimmedEmail, password, displayName.trim() || undefined);
            setSuccess(true);
        },
        [email, password, displayName, linkGuest]
    );

    if (success && !error) {
        return (
            <div className="confirm-modal-overlay" onClick={onClose}>
                <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
                    <h3 className="confirm-modal-title">Account created!</h3>
                    <p className="confirm-modal-message">
                        Your guest work has been preserved. Check your email for a verification link.
                    </p>
                    <div className="confirm-modal-actions">
                        <button className="confirm-modal-confirm confirm-modal-confirm--primary" onClick={onClose}>
                            Continue editing
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="confirm-modal-overlay" onClick={onClose}>
            <div className="guest-signup-modal" onClick={(e) => e.stopPropagation()}>
                <h3 className="guest-signup-title">Create an Account</h3>
                <p className="guest-signup-subtitle">
                    Save your work to the cloud and access it from any device.
                </p>

                {displayError && (
                    <div className="login-error" role="alert">
                        {displayError}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="login-form" autoComplete="on">
                    <div className="login-field">
                        <label htmlFor="guest-name">Name (optional)</label>
                        <input
                            type="text"
                            id="guest-name"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            placeholder="Your name"
                            autoComplete="name"
                            maxLength={100}
                            disabled={loading}
                        />
                    </div>

                    <div className="login-field">
                        <label htmlFor="guest-email">Email</label>
                        <input
                            type="email"
                            id="guest-email"
                            value={email}
                            onChange={(e) => { setEmail(e.target.value); setLocalError(null); clearError(); }}
                            placeholder="you@example.com"
                            autoComplete="email"
                            required
                            disabled={loading}
                        />
                    </div>

                    <div className="login-field">
                        <label htmlFor="guest-password">Password</label>
                        <input
                            type="password"
                            id="guest-password"
                            value={password}
                            onChange={(e) => { setPassword(e.target.value); setLocalError(null); }}
                            placeholder="••••••••"
                            autoComplete="new-password"
                            required
                            minLength={8}
                            disabled={loading}
                        />
                    </div>

                    <button type="submit" className="login-submit" disabled={loading}>
                        {loading ? <span className="login-spinner" /> : "Create Account"}
                    </button>
                </form>

                <div className="guest-signup-footer">
                    <button className="login-link" onClick={onClose}>
                        Continue as guest
                    </button>
                </div>
            </div>
        </div>
    );
}
